import React from 'react'
import { removeHtml } from '@fs/zion-frontend-friends'
import { baseUrl } from '@fs/zion-config'
import { getRootsTechOrganizationData } from './OrganizationStructuredData'
import { getVirtualLocationData } from './VirtualLocationStructuredData'
import { getLondonPlaceData, getSaltPalacePlaceData } from './PlaceStructuredData'
import {
  getDiscoveryDayOfferData,
  getInPerson1DayOfferData,
  getInPerson3DayOfferData,
  getOnlineOfferData,
} from './OfferStructuredData'
import StructuredData from './StructuredData'
import { getThingData } from './ThingStructuredData'
import { getBrowserSeoLocales, getCustomImage, isConferencePromo } from '../helpers'
import { stripHtml } from '../../api'
import { getAudienceData } from './AudienceStructuredData'

const virtualYears = ['2021', '2022', '2023', '2024', '2025', '2026']
const inPersonYears = [
  '2011',
  '2012',
  '2013',
  '2014',
  '2015',
  '2016',
  '2017',
  '2018',
  '2019',
  '2020',
  '2021',
  '2022',
  '2023',
  '2024',
  '2025',
  '2026',
]
const londonYears = ['2019']

const getLocations = (year, isLondon = false) => {
  const location = []
  if (!isLondon) {
    if (virtualYears.includes(year)) {
      location.push(getVirtualLocationData())
    }
    if (inPersonYears.includes(year)) {
      location.push(getSaltPalacePlaceData())
    }
  } else if (londonYears.includes(year)) {
    location.push(getLondonPlaceData())
  }
  return location
}

export const getDiscoveryDayEvent = () => {
  return {
    '@type': 'Event',
    name: 'Family Discovery Day',
    description: '',
    startDate: '2026-03-07',
    endDate: '2026-03-07',
    url: `${baseUrl}/rootstech/registration/in-person/discovery-day`,
    location: getSaltPalacePlaceData(),
    organizer: getRootsTechOrganizationData(false),
    offers: getDiscoveryDayOfferData(),
    eventAttendanceMode: 'https://schema.org/MixedEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    performer: undefined, // TODO: list the keynotes
  }
}

export const getRootsTechEvent = () => {
  return {
    '@type': 'Event',
    name: 'RootsTech 2026',
    startDate: '2026-03-05T00:00:00Z',
    endDate: '2026-03-07T00:00:00Z',
    url: `${baseUrl}/rootstech`,
    location: getLocations('2026'),
    organizer: getRootsTechOrganizationData(false),
    eventAttendanceMode: 'https://schema.org/MixedEventAttendanceMode',
    offers: [getOnlineOfferData(), getInPerson3DayOfferData(), getInPerson1DayOfferData()],
    about: getThingData({ name: 'Together' }),
    keywords: 'RootsTech 2026, Together, Family History',
    subEvent: getDiscoveryDayEvent(),
    eventStatus: 'https://schema.org/EventScheduled',
    performer: undefined, // TODO: list the keynotes
    audience: [
      getAudienceData('PublicAudience'),
      getAudienceData('BusinessAudience'),
      getAudienceData('EducationalAudience'),
    ],
    typicalAgeRange: '0-99',
    accessibilityFeature: ['WheelchairAccessible', 'SignLanguageInterpreter', 'ClosedCaptioning'],
  }
}

export const getRootsTechEvents = () => {
  return [
    getRootsTechEvent(),
    {
      '@type': 'Event',
      name: 'RootsTech 2025',
      startDate: '2025-03-06',
      endDate: '2025-03-08',
      url: `${baseUrl}/rootstech/search?f.conferenceYear=2025`,
      location: getLocations('2025'),
      organizer: getRootsTechOrganizationData(false),
      eventAttendanceMode: 'https://schema.org/MixedEventAttendanceMode',
      about: getThingData({ name: 'Discover' }),
      keywords: 'RootsTech 2025, Discover, Family History',
      audience: [
        getAudienceData('PublicAudience'),
        getAudienceData('BusinessAudience'),
        getAudienceData('EducationalAudience'),
      ],
      typicalAgeRange: '0-99',
      accessibilityFeature: ['WheelchairAccessible', 'SignLanguageInterpreter', 'ClosedCaptioning'],
    },
    {
      '@type': 'Event',
      name: 'RootsTech 2024',
      startDate: '2024-02-29',
      endDate: '2024-03-02',
      url: `${baseUrl}/rootstech/search?f.conferenceYear=2024`,
      location: getLocations('2024'),
      organizer: getRootsTechOrganizationData(false),
      eventAttendanceMode: 'https://schema.org/MixedEventAttendanceMode',
      about: getThingData({ name: 'Remember' }),
      keywords: 'RootsTech 2024, Remember, Family History',
      audience: [
        getAudienceData('PublicAudience'),
        getAudienceData('BusinessAudience'),
        getAudienceData('EducationalAudience'),
      ],
      typicalAgeRange: '0-99',
      accessibilityFeature: ['WheelchairAccessible', 'SignLanguageInterpreter', 'ClosedCaptioning'],
    },
    {
      '@type': 'Event',
      name: 'RootsTech 2023',
      startDate: '2024-03-02',
      endDate: '2024-03-04',
      url: `${baseUrl}/rootstech/search?f.conferenceYear=2023`,
      location: getLocations('2023'),
      organizer: getRootsTechOrganizationData(false),
      eventAttendanceMode: 'https://schema.org/MixedEventAttendanceMode',
      about: getThingData({ name: 'Uniting' }),
      keywords: 'RootsTech 2023, Uniting, Uniting Families, Family History',
      audience: [
        getAudienceData('PublicAudience'),
        getAudienceData('BusinessAudience'),
        getAudienceData('EducationalAudience'),
      ],
      typicalAgeRange: '0-99',
      accessibilityFeature: ['SignLanguageInterpreter', 'ClosedCaptioning'],
    },
    {
      '@type': 'Event',
      name: 'RootsTech 2022',
      startDate: '2022-03-03',
      endDate: '2022-03-05',
      url: `${baseUrl}/rootstech/search?f.conferenceYear=2022`,
      location: getLocations('2022'),
      organizer: getRootsTechOrganizationData(false),
      eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
      about: getThingData({ name: 'Choose Connection' }),
      keywords: 'RootsTech 2022, Choose Connection, Family History',
      audience: [
        getAudienceData('PublicAudience'),
        getAudienceData('BusinessAudience'),
        getAudienceData('EducationalAudience'),
      ],
      typicalAgeRange: '0-99',
      accessibilityFeature: ['SignLanguageInterpreter', 'ClosedCaptioning'],
    },
    {
      '@type': 'Event',
      name: 'RootsTech 2021',
      startDate: '2021-02-25',
      endDate: '2021-02-27',
      url: `${baseUrl}/rootstech/search?f.conferenceYear=2021`,
      location: getLocations('2021'),
      organizer: getRootsTechOrganizationData(false),
      eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
      about: getThingData({ name: 'Connect' }),
      keywords: 'RootsTech 2021, RootsTech Connect, Connect, Family History',
      audience: [
        getAudienceData('PublicAudience'),
        getAudienceData('BusinessAudience'),
        getAudienceData('EducationalAudience'),
      ],
      typicalAgeRange: '0-99',
      accessibilityFeature: ['SignLanguageInterpreter', 'ClosedCaptioning'],
    },
    {
      '@type': 'Event',
      name: 'RootsTech 2020',
      startDate: '2020-02-26',
      endDate: '2020-02-29',
      url: `${baseUrl}/rootstech/search?f.conferenceYear=2020`,
      location: getLocations('2020'),
      organizer: getRootsTechOrganizationData(false),
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      about: getThingData({ name: 'The Story of You' }),
      keywords: 'RootsTech 2020, The Story of You, 10 year celebration, Family History',
      audience: [
        getAudienceData('PublicAudience'),
        getAudienceData('BusinessAudience'),
        getAudienceData('EducationalAudience'),
      ],
      typicalAgeRange: '0-99',
    },
    {
      '@type': 'Event',
      name: 'RootsTech London 2019',
      startDate: '2019-10-24',
      endDate: '2019-10-26',
      url: `${baseUrl}/rootstech/search?f.conferenceYear=2019`,
      location: getLocations('2019', true),
      organizer: getRootsTechOrganizationData(false),
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      about: getThingData({ name: 'Connect. Belong.' }),
      keywords: 'RootsTech London, RootsTech London 2019, Connect. Belong., Family History',
      audience: [
        getAudienceData('PublicAudience'),
        getAudienceData('BusinessAudience'),
        getAudienceData('EducationalAudience'),
      ],
      typicalAgeRange: '0-99',
    },
    {
      '@type': 'Event',
      name: 'RootsTech 2019',
      startDate: '2019-03-27',
      endDate: '2019-03-30',
      url: `${baseUrl}/rootstech/search?f.conferenceYear=2019`,
      location: getLocations('2019'),
      organizer: getRootsTechOrganizationData(false),
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      about: getThingData({ name: 'Connect. Belong.' }),
      keywords: 'RootsTech 2019, Connect. Belong., Family History',
      audience: [
        getAudienceData('PublicAudience'),
        getAudienceData('BusinessAudience'),
        getAudienceData('EducationalAudience'),
      ],
      typicalAgeRange: '0-99',
    },
    {
      '@type': 'Event',
      name: 'RootsTech 2018',
      startDate: '2018-02-28',
      endDate: '2018-03-03',
      location: getLocations('2018'),
      organizer: getRootsTechOrganizationData(false),
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      about: getThingData({ name: 'Connect. Belong.' }),
      keywords: 'RootsTech 2018, Connect. Belong., Family History',
      audience: [
        getAudienceData('PublicAudience'),
        getAudienceData('BusinessAudience'),
        getAudienceData('EducationalAudience'),
      ],
      typicalAgeRange: '0-99',
    },
    {
      '@type': 'Event',
      name: 'RootsTech 2017',
      startDate: '2017-02-08',
      endDate: '2017-02-11',
      location: getLocations('2017'),
      organizer: getRootsTechOrganizationData(false),
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      about: [
        getThingData({ name: 'Gather' }),
        getThingData({ name: 'Food' }),
        getThingData({ name: 'African American Heritage' }),
      ],
      keywords: 'RootsTech 2017, Gather, Food, African American Heritage, Family History',
      audience: [
        getAudienceData('PublicAudience'),
        getAudienceData('BusinessAudience'),
        getAudienceData('EducationalAudience'),
      ],
      typicalAgeRange: '0-99',
    },
    {
      '@type': 'Event',
      name: 'RootsTech 2016',
      startDate: '2016-02-03',
      endDate: '2016-02-06',
      location: getLocations('2016'),
      organizer: getRootsTechOrganizationData(false),
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      about: getThingData({ name: 'Celebrating Families Across Generations' }),
      keywords: 'RootsTech 2016, Celebrating Families Across Generations, Family History',
      audience: [
        getAudienceData('PublicAudience'),
        getAudienceData('BusinessAudience'),
        getAudienceData('EducationalAudience'),
      ],
      typicalAgeRange: '0-99',
    },
    {
      '@type': 'Event',
      name: 'RootsTech 2015',
      startDate: '2015-02-12',
      endDate: '2015-02-14',
      location: getLocations('2015'),
      organizer: getRootsTechOrganizationData(false),
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      about: getThingData({ name: 'Connecting Families Across Generations' }),
      keywords: 'RootsTech 2015, Connecting Families Across Generations, Family History',
      audience: [
        getAudienceData('PublicAudience'),
        getAudienceData('BusinessAudience'),
        getAudienceData('EducationalAudience'),
      ],
      typicalAgeRange: '0-99',
    },
    {
      '@type': 'Event',
      name: 'RootsTech 2014',
      startDate: '2014-02-06',
      endDate: '2014-02-08',
      location: getLocations('2014'),
      organizer: getRootsTechOrganizationData(false),
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      about: getThingData({ name: 'Discovering Families Past, Present and Future' }),
      keywords: 'RootsTech 2014, Discovering Families Past, Present and Future, Family History',
      audience: [
        getAudienceData('PublicAudience'),
        getAudienceData('BusinessAudience'),
        getAudienceData('EducationalAudience'),
      ],
      typicalAgeRange: '0-99',
    },
    {
      '@type': 'Event',
      name: 'RootsTech 2013',
      startDate: '2013-03-21',
      endDate: '2013-03-23',
      location: getLocations('2013'),
      organizer: getRootsTechOrganizationData(false),
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      about: getThingData({ name: 'Story' }),
      keywords: 'RootsTech 2013, Story, Family History',
      audience: [
        getAudienceData('PublicAudience'),
        getAudienceData('BusinessAudience'),
        getAudienceData('EducationalAudience'),
      ],
      typicalAgeRange: '0-99',
    },
    {
      '@type': 'Event',
      name: 'RootsTech 2012',
      startDate: '2012-02-02',
      endDate: '2012-02-04',
      location: getLocations('2012'),
      organizer: getRootsTechOrganizationData(false),
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      about: getThingData({ name: '1940 Census' }),
      keywords: 'RootsTech 2012, 1940 Census, Family History',
      audience: [
        getAudienceData('PublicAudience'),
        getAudienceData('BusinessAudience'),
        getAudienceData('EducationalAudience'),
      ],
      typicalAgeRange: '0-99',
    },
    {
      '@type': 'Event',
      name: 'RootsTech 2011',
      startDate: '2011-02-10',
      endDate: '2011-02-12',
      location: getLocations('2011'),
      organizer: getRootsTechOrganizationData(false),
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      keywords: 'RootsTech 2011, Family History',
      audience: [
        getAudienceData('PublicAudience'),
        getAudienceData('BusinessAudience'),
        getAudienceData('EducationalAudience'),
      ],
      typicalAgeRange: '0-99',
    },
  ]
}

export const getCallForExhibitorsEventData = (data) => {
  return {
    '@type': 'BusinessEvent',
    name: data.name,
    description: stripHtml(data.description),
    eventType: 'ExhibitorRecruitment',
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    location: getVirtualLocationData(),
    organizer: getRootsTechOrganizationData(false),
    url: `${baseUrl}/rootstech/expohall/sponsor`,
    superEvent: getRootsTechEvent(),
    audience: [getAudienceData('BusinessAudience')],
  }
}

export const getCallForContentEventData = () => {
  return {
    '@type': 'EducationEvent',
    name: 'RootsTech 2026 Call for Content',
    description:
      'Submit your session proposals to be considered as a speaker at RootsTech 2026. We are looking for engaging, inspirational, and educational presentations that help attendees discover, connect, and preserve their family stories.',
    startDate: '2025-10-01',
    endDate: '2025-11-15',
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    location: getVirtualLocationData(),
    organizer: getRootsTechOrganizationData(false),
    url: `${baseUrl}/rootstech/call-for-presentations`,
    offers: getOnlineOfferData({ url: `${baseUrl}/rootstech/call-for-presentations` }),
    superEvent: getRootsTechEvent(),
    audience: getAudienceData('EducationalAudience'),
  }
}

export const getRegistrationEventData = () => {
  return {
    '@type': 'Event',
    name: 'RootsTech 2026 Registration',
    description:
      'Register to attend RootsTech 2026, the world’s largest family history conference. Join in person in Salt Lake City or participate virtually from anywhere in the world.',
    startDate: '2025-09-25T00:00:00-06:00',
    endDate: '2026-03-05T23:59:59-07:00',
    eventAttendanceMode: 'https://schema.org/MixedEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    url: `${baseUrl}/rootstech/registration`,
    inLanguage: ['en'],
    location: [getVirtualLocationData(), getSaltPalacePlaceData()],
    organizer: getRootsTechOrganizationData(false),
    offers: [getOnlineOfferData(), getInPerson3DayOfferData(), getInPerson1DayOfferData()],
    superEvent: getRootsTechEvent(),
    audience: getAudienceData('PublicAudience'),
  }
}

export const get365Event = (data) => {
  const { title, description, date, conferenceYear, url, image, creators, contentLocale, alternateLocales } = data
  return {
    '@type': 'Event',
    name: title,
    description: removeHtml(description),
    startDate: date || conferenceYear || '',
    url: url || '',
    image: getCustomImage(image),
    location: getVirtualLocationData({ url }),
    organizer: getRootsTechOrganizationData(false),
    performer: creators && creators.length > 0 ? { name: creators[0].name } : undefined,
    offers: { url },
    inLanguage: getBrowserSeoLocales(contentLocale || alternateLocales),
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    about: getThingData({ name: 'RootsTech 365' }),
    keywords: 'RootsTech 365, Family History',
    superEvent: {
      '@type': 'EventSeries',
      name: 'RootsTech 365 Events',
      url: `${baseUrl}/rootstech/events`,
    },
  }
}

export const get360Events = (items) =>
  items?.map((item) => {
    return isConferencePromo(item) ? get365Event(item) : []
  }) || []

export const getEventData = (data, locale, type = 'Event') => {
  return {
    '@type': type,
    name: data.name,
    description: data.description,
    startDate: data.startDate,
    endDate: data.endDate,
    url: data.url || `${baseUrl}/rootstech/`,
    location: data.location,
    organizer: data.organizer || getRootsTechOrganizationData(false),
    eventAttendanceMode: 'https://schema.org/MixedEventAttendanceMode',
    offers: [getOnlineOfferData()],
    about: getThingData({ name: 'Together' }),
    inLanguage: locale,
    keywords: 'RootsTech, Together, Family History',
  }
}

export default function EventStructuredData({ data }) {
  if (!data || Object.keys(data).length === 0) return null
  const structuredData = getEventData(data)
  return <StructuredData data={structuredData} dataTestId="event-structured-data" />
}
